import React, { useMemo } from 'react'
import { ArrowRightIcon, Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils.ts'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.tsx'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'
import { useFindAuditLogs } from '@/features/logs/hooks/use-find-audit-logs.ts'

interface DocumentChangesDialogProps {
  transactionId?: string | null
  isOpen: boolean
  onClose: () => void
}

// The actual structure from the audit log trigger
interface ChangeItem {
  path: string
  value_old: unknown
  value_new: unknown
}

interface AuditLog {
  id: string
  table_name: string
  changes: ChangeItem[] | null
  attribute_name?: string
}

const tableNameLabels: Record<string, string> = {
  documents: 'Documento',
  document_attributes: 'Attributo',
}

// Map JSON paths to human-readable labels
const pathLabels: Record<string, string> = {
  value: 'Valore',
  name: 'Nome',
  summary: 'Riepilogo',
  status: 'Stato',
  deleted_at: 'Eliminato',
}

// Paths to hide from the UI
const hiddenPaths = new Set(['updated_at', 'created_at'])

// Check if a value is "empty" (null, undefined, or empty array)
function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (Array.isArray(value) && value.length === 0) return true
  return false
}

// Check if a change is meaningful (not both old and new being empty)
function isMeaningfulChange(change: ChangeItem): boolean {
  // If both old and new are empty, it's not meaningful
  if (isEmptyValue(change.value_old) && isEmptyValue(change.value_new)) {
    return false
  }
  return true
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '-'
  }
  if (typeof value === 'boolean') {
    return value ? 'Sì' : 'No'
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '-'
    }
    // If array contains simple strings/numbers, join them nicely
    if (value.every((v) => typeof v === 'string' || typeof v === 'number')) {
      return value.join(', ')
    }
    // For complex arrays, format as JSON
    return JSON.stringify(value, null, 2)
  }
  if (typeof value === 'object') {
    // For simple objects, try to display nicely
    const str = JSON.stringify(value)
    // If it's short enough, show inline
    if (str.length < 50) {
      return str
    }
    return JSON.stringify(value, null, 2)
  }
  return String(value)
}

function getDisplayTitle(log: AuditLog): string {
  if (log.table_name === 'document_attributes' && log.attribute_name) {
    return log.attribute_name
  }
  return tableNameLabels[log.table_name] || log.table_name
}

function getPathLabel(path: string): string {
  // Handle nested paths like "value.1.Canone"
  const parts = path.split('.')
  const basePath = parts[0]

  // If it's just "value", use the simple label
  if (parts.length === 1) {
    return pathLabels[basePath] || basePath
  }

  // For nested paths, show a simplified version
  // e.g., "value.1.Canone" -> "Canone (riga 2)"
  if (basePath === 'value' && parts.length >= 3) {
    const index = parseInt(parts[1], 10)
    const fieldName = parts.slice(2).join('.')
    if (!isNaN(index)) {
      return `${fieldName} (riga ${index + 1})`
    }
  }

  return path
}

function getVisibleChanges(changes: ChangeItem[] | null): ChangeItem[] {
  if (!changes || !Array.isArray(changes)) return []
  return changes.filter(
    (change) => !hiddenPaths.has(change.path) && isMeaningfulChange(change)
  )
}

export const DocumentChangesDialog: React.FC<DocumentChangesDialogProps> = ({
  transactionId,
  isOpen,
  onClose,
}) => {
  const findAuditLogsQuery = useFindAuditLogs(
    { transaction_id: transactionId! },
    { enabled: isOpen && !!transactionId }
  )

  const auditLogs = findAuditLogsQuery.data?.data as AuditLog[] | undefined

  // Filter and process logs to only show those with visible changes
  const visibleLogs = useMemo(() => {
    if (!auditLogs) return []
    return auditLogs
      .map((log) => ({
        ...log,
        visibleChanges: getVisibleChanges(log.changes),
      }))
      .filter((log) => log.visibleChanges.length > 0)
  }, [auditLogs])

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Dettaglio modifiche</DialogTitle>
          <DialogDescription>
            Elenco delle modifiche apportate al documento
          </DialogDescription>
        </DialogHeader>

        {findAuditLogsQuery.isPending && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="size-6 animate-spin text-gray-400" />
          </div>
        )}

        {findAuditLogsQuery.isError && (
          <div className="py-4 text-center text-sm text-gray-500">
            Impossibile caricare le modifiche
          </div>
        )}

        {visibleLogs.length > 0 && (
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-3">
              {visibleLogs.map((log) => (
                <div key={log.id} className="rounded-lg border p-4">
                  <h4 className="mb-3 text-sm font-medium text-gray-700">
                    {getDisplayTitle(log)}
                  </h4>
                  <div className="space-y-2">
                    {log.visibleChanges.map((change, idx) => {
                      const label = getPathLabel(change.path)
                      const oldValue = formatValue(change.value_old)
                      const newValue = formatValue(change.value_new)
                      const isLongText =
                        oldValue.length > 60 || newValue.length > 60

                      return (
                        <div key={`${change.path}-${idx}`} className="text-sm">
                          <span className="text-xs text-gray-400">{label}</span>
                          {isLongText ? (
                            // Stacked layout for long text
                            <div className="mt-1 space-y-2">
                              <div
                                className={cn(
                                  'rounded px-3 py-2 text-xs',
                                  'bg-red-50 text-red-700'
                                )}
                              >
                                {oldValue}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-400">
                                <ArrowRightIcon className="size-3" />
                                <span>modificato in</span>
                              </div>
                              <div
                                className={cn(
                                  'rounded px-3 py-2 text-xs',
                                  'bg-green-50 text-green-700'
                                )}
                              >
                                {newValue}
                              </div>
                            </div>
                          ) : (
                            // Inline layout for short values
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              <span
                                className={cn(
                                  'break-all rounded px-2 py-0.5 text-xs',
                                  'bg-red-50 text-red-700'
                                )}
                              >
                                {oldValue}
                              </span>
                              <ArrowRightIcon className="size-3 flex-shrink-0 text-gray-400" />
                              <span
                                className={cn(
                                  'break-all rounded px-2 py-0.5 text-xs',
                                  'bg-green-50 text-green-700'
                                )}
                              >
                                {newValue}
                              </span>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}

        {!findAuditLogsQuery.isPending &&
          !findAuditLogsQuery.isError &&
          visibleLogs.length === 0 && (
            <div className="py-4 text-center text-sm text-gray-500">
              Nessuna modifica trovata
            </div>
          )}
      </DialogContent>
    </Dialog>
  )
}
